import {ServiceBase} from '../../ServiceBase';
import {ICmsPagesService} from './cms-pages.service.interface';
import {
  ICmsPageDefinition,
  ICmsPageDefinitionHeadTags,
  IInjectedHeadTag
} from './page-definition/cms-page-definition.interface';
import {action, observable} from 'mobx';
import {IServiceFactoryExtended} from '../../service-factory-extended.interface';
import {CmsPageData} from '../cms-api/cms-api.service.interface';
import {Uri} from '../../location/uri/uri';
import {Check} from "@sparkware/app-utils";
import {UriPath} from "../../location/uri-path/uri-path";
import {UriParams} from "../../location/uri-params/uri-params";

export const LOAD_CONTENT_FROM_DIV_PAGE_DELAY = 250;

export class CmsPagesService extends ServiceBase implements ICmsPagesService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    this.reaction(
        () => this.services.location.currentUri,
        (uri) => this._loadCmsPage(uri),
        { fireImmediately: true }
        );

    this.reaction(
        () => this.services.user.isAuthorized,
        () => this._reloadCurrentPageContent(),
        { fireImmediately: true }
    );
  }

  private _currentUri: Uri | null = null;
  private _isFirstLoad = true;

  @observable.ref
  currentPage: ICmsPageDefinition | null = null;
  injectedPageMetaTags: IInjectedHeadTag[] = [];

  @action.bound
  private _setCurrentPage(pageDefinition: ICmsPageDefinition | null) {
    this.currentPage = pageDefinition;
  }

  private setCurrentPageMetaTags(headerTags: ICmsPageDefinitionHeadTags[]){

    const prepareElementSelector = (elementType:string, attributes = {}) => {
      const attributesQuery = Object.keys(attributes).reduce((query, attributeName) => {
        if(!Check.isString(attributes[attributeName]) || attributeName === 'tag')
          return query;

        return query + `[${attributeName}="${attributes[attributeName]}"]`;
      }, '');

      return elementType + attributesQuery;
    }

    const setElementAttributes = (element, attributes = {}) => {
      Object.keys(attributes).forEach(attributeName => {
        if(attributeName !== 'tag')
          element.setAttribute(attributeName, attributes[attributeName]);
      });
    }

    const addMetaTags = (headerTags: ICmsPageDefinitionHeadTags[]) => {
          headerTags.forEach(headerTag => {
              const {tag, content, href, ...rest} = headerTag;

              if(tag){
                let metaTag = this.services.dom.querySelector(prepareElementSelector(tag, rest)) as HTMLElement;
                const injectedPageMetaTagPreviousElement = metaTag;

                if(!metaTag){
                  metaTag = this.services.dom.createElement(tag);
                  this.services.dom.head.appendChild(metaTag);
                }

                this.injectedPageMetaTags.push({element: metaTag, previous: injectedPageMetaTagPreviousElement ? (injectedPageMetaTagPreviousElement.cloneNode() as HTMLElement) : undefined});
                setElementAttributes(metaTag, headerTag);
              }
          })
    }

    const removeMetaTags = (injectedMetaTags: IInjectedHeadTag[]) => {
          (injectedMetaTags || []).forEach(({element, previous}) => {
              if(previous){
                  element.parentNode?.replaceChild(previous, element);
              }else{
                  element.remove();
              }
          });
    }

    /**
     * To have more meta tags allowed to be injected from pageContent, add an other Regex here
     */
    const allowedTags = '(link.*(?:\\[rel="canonical"]))|' +
        '(meta.*(?:\\[property="og:updated_time"]))|' +
        '(meta.*(?:\\[property="og:description"]))|' +
        '(meta.*(?:\\[property="og:url"]))|' +
        '(meta.*(?:\\[name="og:title"]))|' +
        '(meta.*(?:\\[name="description"]))'
    ;

    const metaTags = (headerTags || []).filter(({tag,content, ...rest}) => {
      const testIfTagAllowedRegex = new RegExp(allowedTags, 'i');
      return testIfTagAllowedRegex.test(prepareElementSelector(tag, rest));
    });

    if(this.injectedPageMetaTags.length > 0){
      removeMetaTags(this.injectedPageMetaTags);
      this.injectedPageMetaTags = [];
    }

    addMetaTags(metaTags);
  }


  private async _reloadCurrentPageContent(): Promise<void> {
    if(this.currentPage) {
      this._currentUri = null;
      try {
        await this._loadCmsPage(this.services.location.currentUri);
      } catch (err) {
        this.services.logger.error(`Failed to reload CMS page ${this.currentPage.url}`, err);
      }
    }
  }


  private async _loadCmsPage(uri: Uri): Promise<void> {

    if(this._currentUri && this._currentUri.pathName && uri.pathName.equals(this._currentUri.pathName)) {
      return;
    }

    let pageElement = this.services.dom.getElementById('page');
    if(this._isFirstLoad && pageElement && !this.services.routing.isOrbitRoute(uri.pathName)) {
      if(this.services.user.isAuthorized){
        //In case the user is login we must call the API and not use the DOM page since b2c need to get the XX.context to return the right segments,
        //But we must clean the #page div content to avoid duplicate b2c content on the page
        pageElement.remove();
        await this._loadCmsPageFromApi(uri);
      } else{
        this._loadPageContentFromDOM(pageElement, uri);
      }

    } else {
      await this._loadCmsPageFromApi(uri);
    }


    this._isFirstLoad = false;
    this._currentUri = uri;
  }

  private _loadPageContentFromDOM(pageElement: HTMLElement, uri: Uri): void  {
    // we introduced this delay because if some of the scripts in the innerHTML contains a <script src="somescript.js">
    // then this is a blocker and the innerHTML will not include any of the content following <script src="somescript.js">
    setTimeout(() => {
      const newPage = {
        content: pageElement.innerHTML,
        url: uri.pathName.toString()
      };

      pageElement.remove();

      this._setCurrentPage(newPage);
    }, LOAD_CONTENT_FROM_DIV_PAGE_DELAY);
  }

  private async _loadCmsPageFromApi(uri: Uri): Promise<void> {
    let urlQueryParams: UriParams | undefined = undefined;
    if (this.services.device.hybrid.isEmbedded) {
      urlQueryParams = uri.queryParams;
    }

    const pageData = await this._getPageData(uri.pathName, urlQueryParams);

    if (pageData.title) {
      this.services.dom.setDocumentTitle(pageData.title);
    }


    this.setCurrentPageMetaTags(pageData?.headTags);

    if(this.services.routing.isOrbitRoute(uri.pathName)) {
      this._setCurrentPage(null);
    } else {
      this._updateLocation(pageData);
      this._setCurrentPage({
        content: pageData.content,
        headTags: pageData.headTags,
        elementIdToBeScrolledIntoView: uri.hash.value,
        url: uri.pathName.toString()
      });
    }
  }

  private async _getPageData(pathName: UriPath, queryParams?: UriParams): Promise<CmsPageData> {
    try {
      return await this.services.cmsApi.getPageContent(pathName.toString(), queryParams);
    } catch (err) {
      this.services.logger.error(`Failed to get CMS content for page '${pathName.toString()}'; Error details: ${JSON.stringify(err).substring(0, 1000)} ...`);
      return await this.services.cmsApi.get404PageContent();
    }

  }


  private _updateLocation(pageData: CmsPageData): void {
    if(pageData.location) {
      if(pageData.content) {
        this.services.location.replace(pageData.location.toString());
      } else {
        this.services.location.redirect(pageData.location.toString())
      }
    }
  }

}
